/**
 * BotSettingsCard — extracted from SettingsTab.jsx (iter-83).
 *
 * Renders all bot-level numeric / boolean settings: risk per trade, risk-reward
 * ratio, max open positions, daily/monthly loss limits, news buffer, loop
 * interval, daily profit target, quick-TP, manual lot size, news/AI toggles,
 * and the "Save Settings" button.
 */
import { motion } from 'framer-motion';
import { Settings, Save, Loader2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Switch } from '../../../components/ui/switch';

export default function BotSettingsCard({ form, setForm, handleSave, saving, t, language }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/[0.02] backdrop-blur-xl rounded-xl border border-white/10 p-6"
    >
      <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
        <Settings className="h-5 w-5 text-[#d4af37]" />
        {t.botSettings}
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div>
          <label className="block text-sm text-[#71717A] mb-2">{t.riskPerTrade}</label>
          <Input
            type="number"
            step="0.1"
            value={form.risk_per_trade_pct}
            onChange={(e) => setForm({ ...form, risk_per_trade_pct: parseFloat(e.target.value) })}
            className="bg-white/5 border-white/10 text-white"
          />
        </div>

        <div>
          <label className="block text-sm text-[#71717A] mb-2">{t.riskReward}</label>
          <Input
            type="number"
            step="0.1"
            value={form.risk_reward_ratio}
            onChange={(e) => setForm({ ...form, risk_reward_ratio: parseFloat(e.target.value) })}
            className="bg-white/5 border-white/10 text-white"
          />
        </div>

        <div>
          <label className="block text-sm text-[#71717A] mb-2">{t.maxOpenPositions}</label>
          <Input
            type="number"
            value={form.max_open_positions}
            onChange={(e) => setForm({ ...form, max_open_positions: parseInt(e.target.value) })}
            className="bg-white/5 border-white/10 text-white"
          />
        </div>

        <div>
          <label className="block text-sm text-[#71717A] mb-2">{t.dailyLossLimit}</label>
          <Input
            type="number"
            step="0.1"
            value={form.daily_loss_limit_pct}
            onChange={(e) => setForm({ ...form, daily_loss_limit_pct: parseFloat(e.target.value) })}
            className="bg-white/5 border-white/10 text-white"
          />
        </div>

        <div>
          <label className="block text-sm text-[#71717A] mb-2 flex items-center gap-1.5">
            {t.monthlyLossLimit}
            <span className="text-[10px] text-[#d4af37]/70">({language === 'ar' ? 'إيقاف تلقائي' : 'auto-stop'})</span>
          </label>
          <Input
            type="number"
            step="0.5"
            min="0"
            value={form.monthly_loss_limit_pct}
            onChange={(e) => setForm({ ...form, monthly_loss_limit_pct: parseFloat(e.target.value) })}
            className="bg-white/5 border-white/10 text-white"
            data-testid="monthly-loss-limit-input"
          />
        </div>

        <div>
          <label className="block text-sm text-[#71717A] mb-2">{t.newsBufferMinutes}</label>
          <Input
            type="number"
            value={form.news_buffer_minutes}
            onChange={(e) => setForm({ ...form, news_buffer_minutes: parseInt(e.target.value) })}
            className="bg-white/5 border-white/10 text-white"
          />
        </div>

        <div>
          <label className="block text-sm text-[#71717A] mb-2">{t.loopInterval}</label>
          <Input
            type="number"
            value={form.loop_interval_seconds}
            onChange={(e) => setForm({ ...form, loop_interval_seconds: parseInt(e.target.value) })}
            className="bg-white/5 border-white/10 text-white"
          />
        </div>

        {/* ====== NEW: Daily Profit Target ====== */}
        <div className="col-span-1 md:col-span-2 lg:col-span-3 border-t border-white/5 pt-4 mt-2">
          <h4 className="text-sm font-bold text-[#d4af37] mb-3 flex items-center gap-2">
            <span>🎯</span>
            {language === 'ar' ? 'أهداف الربح والحجم' : 'Profit & Lot Controls'}
            <span className="text-[10px] text-white/40 font-normal">
              {language === 'ar' ? '(إعدادات قوية لتحكم أفضل)' : '(Power controls)'}
            </span>
          </h4>
        </div>

        <div>
          <label className="block text-sm text-[#71717A] mb-2 flex items-center gap-1.5">
            {language === 'ar' ? 'هدف الربح اليومي ($)' : 'Daily Profit Target ($)'}
            <span className="text-[10px] text-emerald-400/70">{language === 'ar' ? 'يوقف اليوم تلقائياً' : 'auto-pause'}</span>
          </label>
          <Input
            type="number"
            step="1"
            min="0"
            value={form.daily_profit_target_usd}
            onChange={(e) => setForm({ ...form, daily_profit_target_usd: parseFloat(e.target.value) || 0 })}
            className="bg-white/5 border-white/10 text-white"
            data-testid="daily-profit-target-input"
            placeholder="0 = disabled"
          />
          <p className="text-[10px] text-[#71717A] mt-1">
            {language === 'ar' ? 'مثال: 200 = البوت يوقف اليوم عند ربح $200' : 'Ex: 200 = bot pauses at +$200 profit today'}
          </p>
        </div>

        <div>
          <label className="block text-sm text-[#71717A] mb-2 flex items-center gap-1.5">
            {language === 'ar' ? 'إغلاق سريع عند ربح ($)' : 'Quick-TP per trade ($)'}
            <span className="text-[10px] text-emerald-400/70">{language === 'ar' ? 'لكل صفقة' : 'per position'}</span>
          </label>
          <Input
            type="number"
            step="0.5"
            min="0"
            value={form.quick_tp_usd}
            onChange={(e) => setForm({ ...form, quick_tp_usd: parseFloat(e.target.value) || 0 })}
            className="bg-white/5 border-white/10 text-white"
            data-testid="quick-tp-input"
            placeholder="0 = disabled"
          />
          <p className="text-[10px] text-[#71717A] mt-1">
            {language === 'ar' ? 'مثال: 7 = أي صفقة تربح 7$ تُغلق فوراً' : 'Ex: 7 = close any trade at +$7 profit'}
          </p>
        </div>

        <div>
          <label className="block text-sm text-[#71717A] mb-2 flex items-center gap-1.5">
            {language === 'ar' ? 'حجم اللوت اليدوي' : 'Manual Lot Size'}
            <span className="text-[10px] text-amber-400/70">{language === 'ar' ? 'يتجاوز %المخاطرة' : 'overrides risk%'}</span>
          </label>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={form.manual_lot_size}
            onChange={(e) => setForm({ ...form, manual_lot_size: parseFloat(e.target.value) || 0 })}
            className="bg-white/5 border-white/10 text-white"
            data-testid="manual-lot-input"
            placeholder="0 = auto-calc"
          />
          <p className="text-[10px] text-[#71717A] mt-1">
            {language === 'ar' ? '0 = حساب تلقائي من نسبة المخاطرة | 0.01 = ميكرو لوت ثابت' : '0 = auto from risk% | 0.01 = fixed micro-lot'}
          </p>
        </div>

        <div className="flex items-center justify-between p-3 bg-white/[0.02] rounded-lg border border-white/5 hover:border-[#d4af37]/30 transition-colors">
          <span className="text-white text-sm font-medium">{t.useNewsFilter}</span>
          <Switch
            checked={form.use_news_filter}
            onCheckedChange={(checked) => setForm({ ...form, use_news_filter: checked })}
            className="data-[state=checked]:bg-[#d4af37] data-[state=unchecked]:bg-white/10"
            data-testid="news-filter-switch"
          />
        </div>

        <div className="flex items-center justify-between p-3 bg-white/[0.02] rounded-lg border border-white/5 hover:border-[#d4af37]/30 transition-colors">
          <span className="text-white text-sm font-medium">{t.useAIConfirmation}</span>
          <Switch
            checked={form.use_ai_confirmation}
            onCheckedChange={(checked) => setForm({ ...form, use_ai_confirmation: checked })}
            className="data-[state=checked]:bg-[#d4af37] data-[state=unchecked]:bg-white/10"
            data-testid="ai-confirm-switch"
          />
        </div>
      </div>

      <div className="mt-6">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#d4af37] hover:bg-[#c9a227] text-black"
          data-testid="save-settings-btn"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          {t.saveSettings}
        </Button>
      </div>
    </motion.div>  );
}
